import { useCallback, useEffect, useRef, useState } from 'react';
import { ProductImage } from '../../models';
import { safeImageUrl, thumbnailUrlOf } from '../../utils/url';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useFocusTrap } from '../../hooks/useFocusTrap';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

interface ProductLightboxProps {
  images: ProductImage[];
  currentUrl: string;
  productName: string;
  onChange: (imageUrl: string) => void;
  onClose: () => void;
}

const CONTROL_CLASS =
  'w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-white';

export function ProductLightbox({ images, currentUrl, productName, onChange, onClose }: ProductLightboxProps) {
  const [zoomLevel, setZoomLevel] = useState(MIN_ZOOM);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const dialogRef = useRef<HTMLDivElement>(null);

  useBodyScrollLock(true);
  useFocusTrap(dialogRef, true, onClose);

  const resetView = useCallback(() => {
    setZoomLevel(MIN_ZOOM);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    setZoomLevel((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel((z) => {
      const next = Math.max(z - ZOOM_STEP, MIN_ZOOM);
      if (next === MIN_ZOOM) setPanOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const goTo = useCallback(
    (direction: 1 | -1) => {
      if (images.length <= 1) return;
      const currentIdx = images.findIndex((img) => img.imageUrl === currentUrl);
      const nextIdx = (Math.max(currentIdx, 0) + direction + images.length) % images.length;
      onChange(images[nextIdx].imageUrl);
      resetView();
    },
    [images, currentUrl, onChange, resetView],
  );

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goTo(-1);
      if (e.key === 'ArrowRight') goTo(1);
      if (e.key === '+' || e.key === '=') zoomIn();
      if (e.key === '-') zoomOut();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goTo, zoomIn, zoomOut]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (zoomLevel <= MIN_ZOOM) return;
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, offsetX: panOffset.x, offsetY: panOffset.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isPanning) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    setPanOffset({ x: panStartRef.current.offsetX + dx, y: panStartRef.current.offsetY + dy });
  };

  const stopPanning = () => setIsPanning(false);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Imagem ampliada de ${productName}`}
      tabIndex={-1}
      className="fixed inset-0 z-sheet bg-black/90 flex items-center justify-center focus:outline-none"
      onClick={onClose}
    >
      <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
        <span className="text-white/80 text-sm mr-2" aria-live="polite">
          {Math.round(zoomLevel * 100)}%
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            zoomOut();
          }}
          disabled={zoomLevel <= MIN_ZOOM}
          aria-label="Diminuir zoom"
          className={CONTROL_CLASS}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
          </svg>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            zoomIn();
          }}
          disabled={zoomLevel >= MAX_ZOOM}
          aria-label="Aumentar zoom"
          className={CONTROL_CLASS}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
          </svg>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Fechar"
          className={CONTROL_CLASS}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goTo(-1);
            }}
            aria-label="Imagem anterior"
            className={`${CONTROL_CLASS} absolute left-3 top-1/2 -translate-y-1/2 z-10`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goTo(1);
            }}
            aria-label="Próxima imagem"
            className={`${CONTROL_CLASS} absolute right-3 top-1/2 -translate-y-1/2 z-10`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      <div
        className="max-w-[90vw] max-h-[85vh] overflow-hidden select-none"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopPanning}
        onPointerCancel={stopPanning}
        style={{ cursor: zoomLevel > MIN_ZOOM ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
      >
        <img
          src={safeImageUrl(currentUrl)}
          alt={productName}
          className="max-w-[90vw] max-h-[85vh] object-contain transition-transform duration-200"
          style={{
            transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
          }}
          draggable={false}
        />
      </div>

      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {images.map((img, idx) => (
            <button
              key={img.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(img.imageUrl);
                resetView();
              }}
              aria-label={`Ver imagem ${idx + 1} de ${images.length}`}
              aria-current={currentUrl === img.imageUrl}
              className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                currentUrl === img.imageUrl ? 'border-white' : 'border-white/30 hover:border-white/60'
              }`}
            >
              <img src={safeImageUrl(thumbnailUrlOf(img))} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
